import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Get API base URL based on environment
 * For embedded widgets, we need to detect the correct API server
 */
const getDefaultApiBaseUrl = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') return '';

  // For embedded widgets, detect from script source
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src && scripts[i].src.includes('smushchat-widget.js')) {
      if (scripts[i].src.includes('localhost') || scripts[i].src.includes('127.0.0.1')) {
        return 'http://localhost:3000';
      } else if (scripts[i].src.includes('staging.smushlabs.ai')) {
        return 'https://staging.smushlabs.ai';
      } else if (scripts[i].src.includes('smushlabs.ai')) {
        return 'https://smushlabs.ai';
      } else if (scripts[i].src.includes('workers.dev')) {
        const url = new URL(scripts[i].src);
        return url.origin;
      }
    }
  }

  // Fallback: use current origin (works for platform mode)
  return window.location.origin;
};

export function useChatMessages(introMessageText, threadId, chatbotId, getApiBaseUrl = getDefaultApiBaseUrl) {
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const initializedRef = useRef(false);
  const lastThreadIdRef = useRef(threadId);

  // Define saveMessagesToDB BEFORE it is used in useEffect
  const saveQueueRef = useRef(Promise.resolve());

  const saveMessagesToDB = useCallback((userMessage, assistantMessage, usedThreadId, chatbotId) => {
    if (!usedThreadId || !chatbotId) return;

    // Get the correct API base URL
    const baseUrl = typeof getApiBaseUrl === 'function' ? getApiBaseUrl() : '';
    const apiEndpoint = `${baseUrl}/api/messages`;
    const pageUrl = typeof window !== 'undefined' ? window.location.href : null;

    // Enqueue the save operation
    saveQueueRef.current = saveQueueRef.current.then(async () => {
      try {
        // 1. Save User Message
        if (userMessage) {
          await fetch(apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              threadId: usedThreadId,
              role: 'user',
              content: userMessage.content,
              chatbotId: chatbotId,
              pageUrl: pageUrl,
            }),
          });
        }

        // 2. Save Assistant Message (if exists)
        if (assistantMessage) {
          await fetch(apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              threadId: usedThreadId,
              role: 'assistant',
              content: assistantMessage.content,
              chatbotId: chatbotId,
              pageUrl: pageUrl,
            }),
          });
        }
      } catch (err) {
        // Silently fail
      }
    });

    return saveQueueRef.current;
  }, [getApiBaseUrl]);

  // Allow external actions to be serialized with message saving (e.g., Talk to Human request)
  const addToSaveQueue = useCallback((action) => {
    saveQueueRef.current = saveQueueRef.current.then(async () => {
      try {
        await action();
      } catch (err) {
        // Silently fail
      }
    });
    return saveQueueRef.current;
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      // Include chatbotId in storage key to ensure each chatbot has unique history
      const storageKey = threadId ? `chatMessages_${chatbotId}_${threadId}` : `chatMessages_${chatbotId}_default`;
      localStorage.setItem(storageKey, JSON.stringify(messages));
    }
  }, [messages, threadId, chatbotId]);

  useEffect(() => {
    // Don't reset messages if we're in the middle of a conversation and threadId just changed
    const isThreadIdChange = lastThreadIdRef.current !== threadId && initializedRef.current;

    // Preserve messages even if it's just the intro message (for Talk to Human early clicks)
    const hasActiveConversation = messages.length > 0;

    if (isThreadIdChange && hasActiveConversation) {
      // Just update the ref and save current messages to new threadId
      lastThreadIdRef.current = threadId;
      if (messages.length > 0) {
        const storageKey = threadId ? `chatMessages_${chatbotId}_${threadId}` : `chatMessages_${chatbotId}_default`;
        localStorage.setItem(storageKey, JSON.stringify(messages));
      }
      return;
    }

    // Include chatbotId in storage key to ensure each chatbot has unique history
    const storageKey = threadId ? `chatMessages_${chatbotId}_${threadId}` : `chatMessages_${chatbotId}_default`;

    const savedMessages = localStorage.getItem(storageKey);

    if (savedMessages) {
      // Always use saved messages if they exist
      setMessages(JSON.parse(savedMessages));
    } else {
      // Only create initial messages with custom intro if no saved messages exist
      const finalIntroMessage = introMessageText && introMessageText.trim() !== "" ?
        introMessageText :
        "Hello! How can I help you today?";

      const initialMessages = [
        {
          role: 'assistant',
          content: finalIntroMessage,
          timestamp: new Date().toISOString()
        },
      ];

      setMessages(initialMessages);
      localStorage.setItem(storageKey, JSON.stringify(initialMessages));

      // Persist intro message to DB
      if (threadId && chatbotId) {
        saveMessagesToDB(null, initialMessages[0], threadId, chatbotId);
      }
    }

    // Mark as initialized and update threadId ref
    initializedRef.current = true;
    lastThreadIdRef.current = threadId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introMessageText, threadId, chatbotId, saveMessagesToDB]); // messages intentionally omitted to prevent reset loops

  // Function to force refresh intro message (useful when user updates intro message in settings)
  const refreshIntroMessage = useCallback(() => {
    const storageKey = threadId ? `chatMessages_${chatbotId}_${threadId}` : `chatMessages_${chatbotId}_default`;

    const finalIntroMessage = introMessageText && introMessageText.trim() !== "" ?
      introMessageText :
      "Hello! How can I help you today?";

    const initialMessages = [
      {
        role: 'assistant',
        content: finalIntroMessage,
        timestamp: new Date().toISOString()
      },
    ];

    setMessages(initialMessages);
    localStorage.setItem(storageKey, JSON.stringify(initialMessages));

    // Persist new intro message to DB
    if (threadId && chatbotId) {
      saveMessagesToDB(null, initialMessages[0], threadId, chatbotId);
    }
  }, [introMessageText, threadId, chatbotId, saveMessagesToDB]);

  return {
    messages,
    setMessages,
    isSending,
    setIsSending,
    saveMessagesToDB,
    refreshIntroMessage,
    addToSaveQueue,
  };
}
